/**
 * ScheduleEditorScreen — Edit or fork a retreat schedule.
 *
 * SOLID-01: Leaf component, props only, no infrastructure imports.
 * State and handlers live in useScheduleEditor; sheets in ContainerEditorSheet.
 */
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { BellPatternId } from "@retreat/core";
import { TEST_IDS } from "../testing/testIds";
import {
  ContainerEditorSheet,
  AddContainerPicker,
  formatDuration,
} from "../components/ContainerEditorSheet";
import type { EditorContainer } from "../components/ContainerEditorSheet";
import { useScheduleEditor } from "../hooks/useScheduleEditor";
import type { UseScheduleEditorInput } from "../hooks/useScheduleEditor";
import styles from "./ScheduleEditorScreen.module.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_SCHEDULE_NAME_LENGTH = 100;

// ── Types ─────────────────────────────────────────────────────────────────────

type CommonProps = {
  bellPatterns: BellPatternId[];
  traditions: { id: string; name: string }[];
  onCancel: () => void;
  /** True when the last save attempt failed — shows an inline error, edits are kept */
  saveError?: boolean;
};

export type Props = CommonProps & UseScheduleEditorInput;

// ── SortableRow ───────────────────────────────────────────────────────────────

function SortableRow({
  container,
  dotToken,
  onTap,
}: {
  container: EditorContainer;
  dotToken: string;
  onTap: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: container.id });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={dragStyle} className={styles.containerRow} onClick={onTap}>
      <button
        type="button"
        className={styles.dragHandle}
        aria-label="Drag to reorder"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        ⠿
      </button>
      <span className={styles.typeDot} style={{ background: dotToken }} />
      <span className={styles.containerName}>{container.name}</span>
      <span className={styles.containerDuration}>{formatDuration(container.durationSeconds)}</span>
    </div>
  );
}

// ── ScheduleEditorScreen ──────────────────────────────────────────────────────

export function ScheduleEditorScreen(props: Props) {
  const { bellPatterns, containerTypes, onCancel, saveError } = props;
  const ed = useScheduleEditor(props);

  return (
    <div className={styles.screen} data-testid={TEST_IDS.SCHEDULE_EDITOR_SCREEN}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={onCancel}
          data-testid={TEST_IDS.SCHEDULE_EDITOR_CANCEL_BTN}
        >
          Cancel
        </button>
        <h1 className={styles.headerTitle}>{ed.title}</h1>
        <button
          type="button"
          className={styles.saveBtn}
          onClick={ed.handleSave}
          disabled={ed.containers.length === 0}
          data-testid={TEST_IDS.SCHEDULE_EDITOR_SAVE_BTN}
        >
          Save
        </button>
      </div>

      {saveError && (
        <div className={styles.saveError} data-testid={TEST_IDS.SCHEDULE_EDITOR_SAVE_ERROR}>
          Couldn&rsquo;t save — check your connection and try again. Your edits are still here.
        </div>
      )}

      <div className={styles.nameWrap}>
        <input
          className={styles.nameInput}
          value={ed.name}
          onChange={(e) => ed.setName(e.target.value)}
          placeholder="Schedule name"
          maxLength={MAX_SCHEDULE_NAME_LENGTH}
          data-testid={TEST_IDS.SCHEDULE_EDITOR_NAME_INPUT}
        />
      </div>

      <DndContext sensors={ed.sensors} collisionDetection={closestCenter} onDragEnd={ed.handleDragEnd}>
        <SortableContext items={ed.containers.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <div
            className={styles.containerList}
            data-testid={TEST_IDS.SCHEDULE_EDITOR_CONTAINER_LIST}
          >
            {ed.containers.map((c) => (
              <SortableRow
                key={c.id}
                container={c}
                dotToken={ed.dotForType(c.type)}
                onTap={() => ed.setEditingId(c.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        className={styles.addBtn}
        onClick={() => ed.setShowAddPicker(true)}
        data-testid={TEST_IDS.SCHEDULE_EDITOR_ADD_BTN}
      >
        + Add session
      </button>

      {ed.editingContainer !== null && (
        <ContainerEditorSheet
          container={ed.editingContainer}
          bellPatterns={bellPatterns}
          containerTypes={containerTypes}
          onUpdate={ed.updateContainer}
          onDelete={() => ed.deleteContainer(ed.editingContainer!.id)}
          onClose={() => ed.setEditingId(null)}
        />
      )}

      {ed.showAddPicker && (
        <AddContainerPicker
          containerTypes={containerTypes}
          onPick={ed.addContainer}
          onClose={() => ed.setShowAddPicker(false)}
        />
      )}
    </div>
  );
}
